import axios, { AxiosError, AxiosInstance } from 'axios'

const API_URL = '/api'

export interface ApiError {
  error: string
  details?: string
}

// Debug API Types
export interface DebugConfig {
  enabled: boolean
  max_log_lines: number
  allow_search: boolean
  rate_limit: number
}

export interface StreamLogEntry {
  stream_id: string
  stream_name: string
  state: string
  logs: string[]
  log_count: number
  truncated?: boolean
}

export interface DebugLogsResponse {
  total_streams: number
  config: DebugConfig
  streams: StreamLogEntry[]
}

export interface SearchMatch {
  stream_id: string
  stream_name: string
  matches: string[]
  match_count: number
}

export interface DebugSearchResponse {
  query: string
  total_matches: number
  streams: SearchMatch[]
}

export interface StreamTechInfo {
  video_codec?: string
  audio_codec?: string
  resolution?: string
  fps?: string
  bitrate?: string
}

export interface DebugStreamDiagnostics {
  stream_id: string
  stream_name: string
  state: string
  input_url?: string
  output_path?: string
  started_at?: string
  last_activity?: string
  restart_count: number
  tech_info?: StreamTechInfo
  logs: string[]
  log_count: number
  truncated?: boolean
}

export interface DebugHealth {
  instance?: string
  total_active: number
  running: number
  starting?: number
  error?: number
  producing?: number
  healthy: boolean
}

export interface FFmpegProcess {
  id: string
  stream_id: string
  pid: number
  state: string
  input_url?: string
  output_path?: string
  started_at?: string
  last_activity?: string
  restart_count?: number
  bitrate?: string
  speed?: string
  frame?: number
  video_codec?: string
  audio_codec?: string
  resolution?: string
  fps?: string
}

export interface DebugFFmpegProcesses {
  count: number
  processes: FFmpegProcess[]
}

class ApiClient {
  private client: AxiosInstance
  private accessToken: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const token = this.accessToken || localStorage.getItem('access_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config
        if (error.response?.status === 401 && originalRequest) {
          try {
            const refreshToken = localStorage.getItem('refresh_token')
            if (refreshToken) {
              const { data } = await axios.post(`${API_URL}/auth/refresh`, {
                refresh_token: refreshToken,
              })
              this.setTokens(data.access_token, data.refresh_token)
              originalRequest.headers.Authorization = `Bearer ${data.access_token}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            this.clearTokens()
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
  }

  clearTokens() {
    this.accessToken = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  hasToken(): boolean {
    return !!localStorage.getItem('access_token')
  }

  // Auth
  async login(username: string, password: string) {
    const { data } = await this.client.post('/auth/login', { username, password })
    this.setTokens(data.access_token, data.refresh_token)
    return data
  }

  async logout() {
    try {
      await this.client.post('/auth/logout')
    } finally {
      this.clearTokens()
    }
  }

  // Web Player Auth (streaming user login)
  // Note: Does NOT auto-store tokens - let useWatchAuth handle storage
  async userLogin(username: string, password: string): Promise<UserLoginResponse> {
    const { data } = await this.client.post('/auth/user-login', { username, password })
    return data
  }

  // Web Player Channels
  async getWebPlayerChannels(): Promise<WebPlayerChannel[]> {
    const { data } = await this.client.get('/watch/channels')
    return data.channels || []
  }

  // Stop playback - notify server that client has stopped watching
  // streamUrl format: /play/{username}/{password}/{streamID}.m3u8
  async stopPlayback(streamUrl: string): Promise<void> {
    // Convert stream URL to stop URL
    // /play/user/pass/streamID.m3u8 -> /play/user/pass/streamID/stop
    const stopUrl = streamUrl.replace('.m3u8', '/stop')
    try {
      await this.client.post(stopUrl)
    } catch (error) {
      // Don't throw - stopping is best-effort
      console.warn('[API] Failed to notify server of playback stop:', error)
    }
  }

  // Cast Session API - for multiview casting to Chromecast/AirPlay
  async createCastSession(req: CreateCastSessionRequest): Promise<CastSession> {
    const { data } = await this.client.post('/cast/sessions', req)
    return data
  }

  async getCastSession(id: string): Promise<CastSession> {
    const { data } = await this.client.get(`/cast/sessions/${id}`)
    return data
  }

  async updateCastSession(id: string, req: UpdateCastSessionRequest): Promise<CastSession> {
    const { data } = await this.client.put(`/cast/sessions/${id}`, req)
    return data
  }

  async deleteCastSession(id: string): Promise<void> {
    await this.client.delete(`/cast/sessions/${id}`)
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const { data } = await this.client.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
    return data
  }

  // Dashboard
  async getDashboardStats() {
    const { data } = await this.client.get('/dashboard/stats')
    return data
  }

  async getActiveStreams() {
    const { data } = await this.client.get('/dashboard/streams')
    return data
  }

  async getActiveSessions() {
    const { data } = await this.client.get('/dashboard/sessions')
    return data
  }

  async getRecentEvents() {
    const { data } = await this.client.get('/dashboard/events')
    return data
  }

  // Users
  async getUsers(params?: { page?: number; per_page?: number; search?: string }) {
    const { data } = await this.client.get('/users', { params })
    return data
  }

  async getUser(id: string) {
    const { data } = await this.client.get(`/users/${id}`)
    return data
  }

  async createUser(user: CreateUserRequest) {
    const { data } = await this.client.post('/users', user)
    return data
  }

  async updateUser(id: string, user: UpdateUserRequest) {
    const { data } = await this.client.put(`/users/${id}`, user)
    return data
  }

  async deleteUser(id: string) {
    await this.client.delete(`/users/${id}`)
  }

  // Streams
  async getStreams(params?: {
    page?: number
    per_page?: number
    type?: string
    search?: string
    category_id?: string
    is_active?: string  // 'true', 'false', or empty for all
    is_online?: string  // 'true' to filter only online streams
  }) {
    const { data } = await this.client.get('/streams', { params })
    return data
  }

  async getStream(id: string) {
    const { data } = await this.client.get(`/streams/${id}`)
    return data
  }

  async createStream(stream: CreateStreamRequest) {
    const { data } = await this.client.post('/streams', stream)
    return data
  }

  async updateStream(id: string, stream: UpdateStreamRequest) {
    const { data } = await this.client.put(`/streams/${id}`, stream)
    return data
  }

  async deleteStream(id: string) {
    await this.client.delete(`/streams/${id}`)
  }

  async batchUpdateStreams(streamIds: string[], updates: {
    is_active?: boolean
    category_id?: string | null
    custom_user_agent?: string
  }): Promise<{ status: string; updated: number }> {
    const { data } = await this.client.put('/streams/batch', {
      stream_ids: streamIds,
      updates: {
        is_active: updates.is_active,
        category_id: updates.category_id === null ? '' : updates.category_id,
        custom_user_agent: updates.custom_user_agent,
      },
    })
    return data
  }

  async getStreamStats(): Promise<{ total: number; by_type: Record<string, number>; unassigned: number }> {
    const { data } = await this.client.get('/streams/stats')
    return data
  }

  // Categories
  async getCategories(params?: { type?: string }) {
    const { data } = await this.client.get('/categories', { params })
    return data
  }

  async createCategory(category: CreateCategoryRequest) {
    const { data } = await this.client.post('/categories', category)
    return data
  }

  async updateCategory(id: string, category: UpdateCategoryRequest) {
    const { data } = await this.client.put(`/categories/${id}`, category)
    return data
  }

  async deleteCategory(id: string) {
    await this.client.delete(`/categories/${id}`)
  }

  async renumberCategory(id: string) {
    const { data } = await this.client.post(`/categories/${id}/renumber`)
    return data
  }

  async assignChannelNumbers(): Promise<AssignChannelNumbersResponse> {
    const { data } = await this.client.post('/categories/assign-numbers')
    return data
  }

  async getCategoryRanges() {
    const { data } = await this.client.get('/categories/ranges')
    return data
  }

  async validateCategoryRange(rangeStart: number, rangeEnd: number, categoryId?: string) {
    const { data } = await this.client.post('/categories/validate-range', {
      range_start: rangeStart,
      range_end: rangeEnd,
      category_id: categoryId,
    })
    return data
  }

  // Bouquets
  async getBouquets(params?: { page?: number; per_page?: number }) {
    const { data } = await this.client.get('/bouquets', { params })
    return data
  }

  async getBouquet(id: string) {
    const { data } = await this.client.get(`/bouquets/${id}`)
    return data
  }

  async createBouquet(bouquet: CreateBouquetRequest) {
    const { data } = await this.client.post('/bouquets', bouquet)
    return data
  }

  async updateBouquet(id: string, bouquet: UpdateBouquetRequest) {
    const { data } = await this.client.put(`/bouquets/${id}`, bouquet)
    return data
  }

  async deleteBouquet(id: string) {
    await this.client.delete(`/bouquets/${id}`)
  }

  // Series
  async getSeries(params?: { page?: number; per_page?: number; search?: string }) {
    const { data } = await this.client.get('/series', { params })
    return data
  }

  async getSeriesById(id: string) {
    const { data } = await this.client.get(`/series/${id}`)
    return data
  }

  // EPG
  async getEPGSources() {
    const { data } = await this.client.get('/epg/sources')
    return data
  }

  async createEPGSource(source: CreateEPGSourceRequest) {
    const { data } = await this.client.post('/epg/sources', source)
    return data
  }

  async syncEPGSource(id: string) {
    const { data } = await this.client.post(`/epg/sources/${id}/sync`)
    return data
  }

  async deleteEPGSource(id: string) {
    await this.client.delete(`/epg/sources/${id}`)
  }

  async updateEPGSource(id: string, source: { name: string; url: string; type?: string; is_active?: boolean; sync_interval_hours?: number; sync_time?: string | null }) {
    const { data } = await this.client.put(`/epg/sources/${id}`, source)
    return data
  }

  async getEPGChannels() {
    const { data } = await this.client.get('/epg/channels')
    return data
  }

  async getEPGSyncStatus(): Promise<EPGSyncStatusResponse> {
    const { data } = await this.client.get('/epg/sync-status')
    return data
  }

  // Stream testing
  async testStream(id: string) {
    const { data } = await this.client.post(`/streams/${id}/test`)
    return data
  }

  async getStreamLogs(id: string) {
    const { data } = await this.client.get(`/streams/${id}/logs`)
    return data
  }

  async stopTestStream(id: string) {
    const { data } = await this.client.post(`/streams/${id}/stop`)
    return data
  }

  // Stream probing
  async probeURL(url: string) {
    const { data } = await this.client.post('/streams/probe', { url })
    return data
  }

  async probeStream(id: string) {
    const { data } = await this.client.post(`/streams/${id}/probe`)
    return data
  }

  // Stream failover
  async forceFailover(id: string) {
    const { data } = await this.client.post(`/streams/${id}/failover`)
    return data
  }

  async updateSourcePriority(id: string, priorities: number[]) {
    const { data } = await this.client.put(`/streams/${id}/sources/priority`, priorities)
    return data
  }

  // Settings
  async getSettings() {
    const { data } = await this.client.get('/settings')
    return data
  }

  async getSetting(key: string) {
    const { data } = await this.client.get(`/settings/${key}`)
    return data
  }

  async setSetting(key: string, value: any) {
    const { data } = await this.client.put(`/settings/${key}`, { value })
    return data
  }

  async setSettings(settings: Record<string, any>) {
    const { data } = await this.client.put('/settings', settings)
    return data
  }

  async getDefaultUserAgent() {
    const { data } = await this.client.get('/settings/user-agent')
    return data
  }

  async setDefaultUserAgent(userAgent: string) {
    const { data } = await this.client.put('/settings/user-agent', { user_agent: userAgent })
    return data
  }

  // Broadcast Messages (Emby notifications)
  async getBroadcastConfig(): Promise<BroadcastConfig> {
    const { data } = await this.client.get('/broadcast/config')
    return data
  }

  async setBroadcastConfig(config: BroadcastConfigRequest): Promise<void> {
    await this.client.post('/broadcast/config', config)
  }

  async testBroadcastConnection(): Promise<BroadcastTestResponse> {
    const { data } = await this.client.post('/broadcast/test')
    return data
  }

  async sendBroadcast(message: BroadcastSendRequest): Promise<BroadcastSendResponse> {
    const { data } = await this.client.post('/broadcast/send', message)
    return data
  }

  async getBroadcastHistory(): Promise<BroadcastMessage[]> {
    const { data } = await this.client.get('/broadcast/history')
    return data
  }

  async deleteBroadcastMessage(id: string): Promise<void> {
    await this.client.delete(`/broadcast/history/${id}`)
  }

  async clearBroadcastHistory(): Promise<void> {
    await this.client.delete('/broadcast/history')
  }

  // Transcode Profiles
  async getTranscodeProfiles() {
    const { data } = await this.client.get('/transcode/profiles')
    return data
  }

  async getTranscodeProfile(id: string) {
    const { data } = await this.client.get(`/transcode/profiles/${id}`)
    return data
  }

  async createTranscodeProfile(profile: CreateTranscodeProfileRequest) {
    const { data } = await this.client.post('/transcode/profiles', profile)
    return data
  }

  async updateTranscodeProfile(id: string, profile: UpdateTranscodeProfileRequest) {
    const { data } = await this.client.put(`/transcode/profiles/${id}`, profile)
    return data
  }

  async deleteTranscodeProfile(id: string) {
    await this.client.delete(`/transcode/profiles/${id}`)
  }

  async getTranscodePresets() {
    const { data } = await this.client.get('/transcode/presets')
    return data
  }

  async previewTranscodeArgs(profile: CreateTranscodeProfileRequest) {
    const { data } = await this.client.post('/transcode/preview-args', profile)
    return data
  }

  // Sports EPG
  async getSportsSettings() {
    const { data } = await this.client.get('/sports/settings')
    return data
  }

  async updateSportsSettings(settings: UpdateSportsSettingsRequest) {
    const { data } = await this.client.put('/sports/settings', settings)
    return data
  }

  async testSportsPanelConnection(url: string, username: string, password: string) {
    const { data } = await this.client.post('/sports/settings/test-panel', { url, username, password })
    return data
  }

  async testSportsEmbyConnection(url: string, apiKey: string) {
    const { data } = await this.client.post('/sports/settings/test-emby', { url, api_key: apiKey })
    return data
  }

  async getSportsStatus() {
    const { data } = await this.client.get('/sports/status')
    return data
  }

  async triggerSportsRefresh(leagueCode?: string) {
    const params = leagueCode ? { league: leagueCode } : {}
    const { data } = await this.client.post('/sports/refresh', null, { params })
    return data
  }

  async getSportsLeagues() {
    const { data } = await this.client.get('/sports/leagues')
    return data
  }

  async getSportsLeague(code: string) {
    const { data } = await this.client.get(`/sports/leagues/${code}`)
    return data
  }

  async updateSportsLeague(code: string, update: UpdateSportsLeagueRequest) {
    const { data } = await this.client.put(`/sports/leagues/${code}`, update)
    return data
  }

  async getSportsTeams(leagueCode: string) {
    const { data } = await this.client.get(`/sports/leagues/${leagueCode}/teams`)
    return data
  }

  async getSportsTeam(id: string) {
    const { data } = await this.client.get(`/sports/teams/${id}`)
    return data
  }

  async updateSportsTeam(id: string, update: UpdateSportsTeamRequest) {
    const { data } = await this.client.put(`/sports/teams/${id}`, update)
    return data
  }

  async getSportsChannelMappings() {
    const { data } = await this.client.get('/sports/mappings')
    return data
  }

  async createSportsChannelMapping(mapping: CreateSportsChannelMappingRequest) {
    const { data } = await this.client.post('/sports/mappings', mapping)
    return data
  }

  async updateSportsChannelMapping(id: string, update: UpdateSportsChannelMappingRequest) {
    const { data } = await this.client.put(`/sports/mappings/${id}`, update)
    return data
  }

  async deleteSportsChannelMapping(id: string) {
    await this.client.delete(`/sports/mappings/${id}`)
  }

  async getSportsGames(leagueCode?: string) {
    const params = leagueCode ? { league: leagueCode } : {}
    const { data } = await this.client.get('/sports/games', { params })
    return data
  }

  async getSportsLiveGames() {
    const { data } = await this.client.get('/sports/games/live')
    return data
  }

  async getSportsEPGRuns() {
    const { data } = await this.client.get('/sports/runs')
    return data
  }

  // Generic Channels (NFL-style)
  async getSportsGenericChannels(leagueCode: string) {
    const { data } = await this.client.get(`/sports/leagues/${leagueCode}/channels`)
    return data as SportsGenericChannel[]
  }

  async getSportsGenericChannel(id: string) {
    const { data } = await this.client.get(`/sports/channels/${id}`)
    return data as SportsGenericChannel
  }

  async updateSportsGenericChannel(id: string, update: UpdateSportsGenericChannelRequest) {
    const { data } = await this.client.put(`/sports/channels/${id}`, update)
    return data as SportsGenericChannel
  }

  async mapSportsGenericChannelStream(id: string, mapping: MapGenericChannelRequest) {
    const { data } = await this.client.post(`/sports/channels/${id}/map-stream`, mapping)
    return data as SportsGenericChannel
  }

  async getSportsChannelGames(channelId: string) {
    const { data } = await this.client.get(`/sports/channels/${channelId}/games`)
    return data
  }

  // Panel Sources (multi-source support)
  async getPanelSources(): Promise<SportsPanelSource[]> {
    const { data } = await this.client.get('/sports/sources')
    return data
  }

  async getPanelSource(id: string): Promise<SportsPanelSource> {
    const { data } = await this.client.get(`/sports/sources/${id}`)
    return data
  }

  async createPanelSource(source: CreatePanelSourceRequest): Promise<SportsPanelSource> {
    const { data } = await this.client.post('/sports/sources', source)
    return data
  }

  async updatePanelSource(id: string, source: UpdatePanelSourceRequest): Promise<SportsPanelSource> {
    const { data } = await this.client.put(`/sports/sources/${id}`, source)
    return data
  }

  async deletePanelSource(id: string): Promise<void> {
    await this.client.delete(`/sports/sources/${id}`)
  }

  async testPanelSourceConnection(id: string): Promise<PanelSourceTestResult> {
    const { data } = await this.client.post(`/sports/sources/${id}/test`)
    return data
  }

  async setDefaultPanelSource(id: string): Promise<{ message: string }> {
    const { data } = await this.client.post(`/sports/sources/${id}/set-default`)
    return data
  }

  async refreshPanelSource(id: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const { data } = await this.client.post(`/sports/sources/${id}/refresh`)
    return data
  }

  // XUI Import
  async getXUICategories(panelCreds?: { panel_url?: string; panel_username?: string; panel_password?: string }): Promise<XUICategory[]> {
    const { data } = await this.client.get('/import/xui/categories', { params: panelCreds })
    return data
  }

  async getXUIStreams(params?: { category_id?: string; search?: string; panel_url?: string; panel_username?: string; panel_password?: string }): Promise<XUIStreamListResponse> {
    const { data } = await this.client.get('/import/xui/streams', { params })
    return data
  }

  async importXUIStreams(request: ImportStreamsRequest): Promise<ImportStreamsResponse> {
    const { data } = await this.client.post('/import/xui/streams', request)
    return data
  }

  // YouTube Channels
  async getYouTubeChannels(params?: { page?: number; per_page?: number; active?: boolean }): Promise<YouTubeChannelListResponse> {
    const { data } = await this.client.get('/youtube/channels', { params })
    return data
  }

  async getYouTubeChannel(id: string): Promise<YouTubeChannel> {
    const { data } = await this.client.get(`/youtube/channels/${id}`)
    return data
  }

  async createYouTubeChannel(channel: CreateYouTubeChannelRequest): Promise<YouTubeChannel> {
    const { data } = await this.client.post('/youtube/channels', channel)
    return data
  }

  async updateYouTubeChannel(id: string, channel: UpdateYouTubeChannelRequest): Promise<YouTubeChannel> {
    const { data } = await this.client.put(`/youtube/channels/${id}`, channel)
    return data
  }

  async deleteYouTubeChannel(id: string): Promise<void> {
    await this.client.delete(`/youtube/channels/${id}`)
  }

  async refreshYouTubeChannel(id: string): Promise<YouTubeChannel> {
    const { data } = await this.client.post(`/youtube/channels/${id}/refresh`)
    return data
  }

  async getYouTubeChannelVideos(id: string, params?: { page?: number; per_page?: number }): Promise<YouTubeVideoListResponse> {
    const { data } = await this.client.get(`/youtube/channels/${id}/videos`, { params })
    return data
  }

  async getYouTubeChannelStats(id: string): Promise<YouTubeChannelStats> {
    const { data } = await this.client.get(`/youtube/channels/${id}/stats`)
    return data
  }

  async resetYouTubeWatchHistory(id: string): Promise<void> {
    await this.client.post(`/youtube/channels/${id}/reset-history`)
  }

  async parseYouTubeURL(url: string): Promise<{ type: string; id: string; canonical_url: string }> {
    const { data } = await this.client.post('/youtube/parse-url', { url })
    return data
  }

  // Debug API
  async getDebugConfig(): Promise<DebugConfig> {
    const { data } = await this.client.get('/debug/config')
    return data
  }

  async updateDebugConfig(config: DebugConfig): Promise<DebugConfig> {
    const { data } = await this.client.put('/debug/config', config)
    return data
  }

  async getDebugLogs(): Promise<DebugLogsResponse> {
    const { data } = await this.client.get('/debug/logs')
    return data
  }

  async searchDebugLogs(query: string): Promise<DebugSearchResponse> {
    const { data } = await this.client.get('/debug/logs/search', { params: { q: query } })
    return data
  }

  async getDebugStreamDiagnostics(streamId: string): Promise<DebugStreamDiagnostics> {
    const { data } = await this.client.get(`/debug/streams/${streamId}`)
    return data
  }

  async getDebugHealth(): Promise<DebugHealth> {
    const { data } = await this.client.get('/debug/health')
    return data
  }

  async getDebugFFmpegProcesses(): Promise<DebugFFmpegProcesses> {
    const { data } = await this.client.get('/debug/ffmpeg')
    return data
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token')
  }

  // Client Settings & Device Logs (Admin)
  async getUserClientSettings(userId: string): Promise<UserClientSettings> {
    const { data } = await this.client.get(`/admin/users/${userId}/settings`)
    return data
  }

  async updateUserClientSettings(userId: string, settings: UpdateClientSettingsRequest): Promise<UserClientSettings> {
    const { data } = await this.client.patch(`/admin/users/${userId}/settings`, settings)
    return data
  }

  async getUserDeviceLogs(params: {
    userId: string
    level?: string
    tag?: string
    search?: string
    since?: string
    until?: string
    limit?: number
  }): Promise<DeviceLogsResponse> {
    const { userId, ...queryParams } = params
    const { data } = await this.client.get('/logs', { params: { userId, ...queryParams } })
    return data
  }

  async getUserLogTags(userId: string): Promise<{ tags: string[] }> {
    const { data } = await this.client.get('/logs/tags', { params: { userId } })
    return data
  }

  async deleteUserLogs(userId: string): Promise<{ message: string; deleted: number }> {
    const { data } = await this.client.delete(`/admin/users/${userId}/logs`)
    return data
  }
}

export const api = new ApiClient()

// Request/Response types
export interface CreateUserRequest {
  username: string
  password: string
  max_connections?: number
  expires_at?: string
  is_active?: boolean
  bouquet_ids?: string[]
  web_player_enabled?: boolean
}

export interface UpdateUserRequest {
  password?: string
  max_connections?: number
  expires_at?: string
  is_active?: boolean
  bouquet_ids?: string[]
  web_player_enabled?: boolean
}

// Web Player types
export interface WebPlayerUser {
  id: string
  username: string
  web_player_enabled: boolean
}

export interface UserLoginResponse {
  access_token: string
  refresh_token: string
  user: WebPlayerUser
}

export interface WebPlayerChannel {
  id: string
  name: string
  stream_url: string
  icon_url?: string
  category?: string
  category_id?: string
  number?: number
}

// Cast Session types for multiview casting
export interface CastSessionTile {
  slot: number
  stream_id: string
}

export interface CreateCastSessionRequest {
  layout: string
  tiles: CastSessionTile[]
  active_audio_slot: number
}

export interface UpdateCastSessionRequest {
  layout?: string
  tiles?: CastSessionTile[]
  active_audio_slot?: number
}

export interface CastSession {
  id: string
  user_id: string
  layout: string
  tiles: CastSessionTile[]
  active_audio_slot: number
  playback_token: string
  playback_url: string
  state: string
  error_message?: string
  created_at: string
  updated_at: string
}

export interface CreateStreamRequest {
  name: string
  type: 'live' | 'movie' | 'series'
  category_id?: string
  sources: StreamSource[]
  icon_url?: string
  epg_channel_id?: string
  is_direct_source?: boolean
  is_active?: boolean
  fix_timestamps?: boolean
  startup_mode?: 'on_demand' | 'always_on'
  custom_user_agent?: string
  notes?: string
  channel_number?: number
}

export interface UpdateStreamRequest extends Partial<CreateStreamRequest> {}

export interface StreamSource {
  url: string
  priority: number
}

export interface CreateCategoryRequest {
  name: string
  type: 'live' | 'movie' | 'series'
  parent_id?: string
  icon_url?: string
  sort_order?: number
  is_active?: boolean
  channel_range_start?: number | null
  channel_range_end?: number | null
  stable_numbering?: boolean
  auto_assign_numbers?: boolean
}

export interface UpdateCategoryRequest extends Partial<CreateCategoryRequest> {}

export interface CreateBouquetRequest {
  name: string
  description?: string
  stream_ids?: string[]
  category_ids?: string[]
  is_active?: boolean
}

export interface UpdateBouquetRequest extends Partial<CreateBouquetRequest> {}

export interface CreateEPGSourceRequest {
  name: string
  url: string
  type?: string
  sync_interval_hours?: number
  sync_time?: string | null // "HH:MM" UTC for daily scheduled sync
}

export interface CreateTranscodeProfileRequest {
  name: string
  preset?: 'passthrough' | 'universal' | 'low_bandwidth' | 'audio_only' | 'custom'
  description?: string
  video_codec?: string
  video_width?: number
  video_height?: number
  video_bitrate?: number
  video_fps?: number
  video_profile?: string
  audio_codec?: string
  audio_bitrate?: number
  audio_sample_rate?: number
  audio_channels?: number
  custom_ffmpeg_args?: string
}

export interface UpdateTranscodeProfileRequest extends Partial<CreateTranscodeProfileRequest> {}

export interface TranscodeProfile {
  id: string
  name: string
  preset: string
  description?: string
  is_system: boolean
  video_codec?: string
  video_width?: number
  video_height?: number
  video_bitrate?: number
  video_fps?: number
  video_profile?: string
  audio_codec?: string
  audio_bitrate?: number
  audio_sample_rate?: number
  audio_channels?: number
  custom_ffmpeg_args?: string
  created_at: string
  updated_at: string
}

// Sports EPG types
export interface SportsSettings {
  emby_enabled: boolean
  emby_url: string
  emby_api_key: string
  panel_url: string
  panel_username: string
  panel_password: string
  epg_output_dir: string
  timezone: string
}

export interface UpdateSportsSettingsRequest {
  emby_enabled?: boolean
  emby_url?: string
  emby_api_key?: string
  panel_url?: string
  panel_username?: string
  panel_password?: string
  epg_output_dir?: string
  timezone?: string
}

export interface SportsLeague {
  id: string
  code: string
  name: string
  short_name: string
  espn_slug: string
  enabled: boolean
  panel_category?: string
  panel_source_id?: string
  panel_source?: SportsPanelSource
  timezone: string
  update_interval_seconds: number
  epg_refresh_hours: number
  game_duration_hours: number
  icon_url?: string
  last_epg_generation?: string
  last_score_update?: string
  use_generic_channels: boolean
  generic_channel_count: number
  created_at: string
  updated_at: string
  total_teams?: number
  enabled_teams?: number
  mapped_teams?: number
  today_games?: number
  live_games?: number
}

export interface UpdateSportsLeagueRequest {
  enabled?: boolean
  panel_category?: string
  panel_source_id?: string | null
  timezone?: string
  update_interval_seconds?: number
  epg_refresh_hours?: number
  use_generic_channels?: boolean
  generic_channel_count?: number
}

export interface SportsTeam {
  id: string
  league_id: string
  espn_team_id?: string
  name: string
  display_name?: string
  abbreviation?: string
  city?: string
  mascot?: string
  search_terms: string[]
  exclude_terms: string[]
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  conference?: string
  division?: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
  mapping_id?: string
  mapped_stream_id?: string
  mapped_stream_name?: string
  mapping_enabled: boolean
}

export interface UpdateSportsTeamRequest {
  is_active?: boolean
  display_name?: string
  search_terms?: string[]
  exclude_terms?: string[]
  sort_order?: number
}

export interface SportsChannelMapping {
  id: string
  team_id: string
  stream_id: string
  enabled: boolean
  epg_channel_number?: number
  custom_channel_name?: string
  created_at: string
  updated_at: string
  team?: SportsTeam
  stream?: { id: string; name: string }
}

export interface CreateSportsChannelMappingRequest {
  team_id: string
  stream_id: string
  epg_channel_number?: number
  custom_channel_name?: string
}

export interface UpdateSportsChannelMappingRequest {
  stream_id?: string
  enabled?: boolean
  epg_channel_number?: number
  custom_channel_name?: string
}

export interface SportsGame {
  id: string
  team_id: string
  league_id: string
  game_date: string
  espn_game_id?: string
  opponent_name?: string
  opponent_abbr?: string
  is_home: boolean
  game_time?: string
  game_end_time?: string
  venue?: string
  broadcast: string[]
  home_score: number
  away_score: number
  period?: string
  clock?: string
  possession?: string
  is_red_zone: boolean
  game_status: string
  status_detail?: string
  panel_stream_id?: string
  panel_stream_url?: string
  matched_at?: string
  match_confidence?: number
  created_at: string
  updated_at: string
}

export interface SportsStatus {
  running: boolean
  last_update?: string
  next_update?: string
  active_games: number
  enabled_leagues: number
  total_mapped_channels: number
  league_statuses: LeagueStatus[]
  recent_errors?: string[]
  emby_connected: boolean
  panel_connected: boolean
  panel_sources?: PanelSourceStatus[]
}

export interface LeagueStatus {
  code: string
  name: string
  enabled: boolean
  last_epg_generation?: string
  last_score_update?: string
  active_games: number
  // Team-based leagues
  team_count: number
  mapped_teams: number
  // Generic channel leagues (NFL)
  use_generic_channels: boolean
  channel_count: number
  mapped_channels: number
}

export interface SportsEPGRun {
  id: string
  league_id?: string
  run_type: string
  started_at: string
  completed_at?: string
  games_processed: number
  games_matched: number
  errors: number
  status: string
  error_message?: string
}

// Generic Channels (NFL-style)
export interface SportsGenericChannel {
  id: string
  league_id: string
  channel_number: number
  channel_name: string
  panel_stream_id?: string
  panel_stream_name?: string
  stream_id?: string
  stream_name?: string
  enabled: boolean
  sort_order: number
  created_at: string
  updated_at: string
  current_game?: SportsChannelGame
}

export interface SportsChannelGame {
  id: string
  channel_id: string
  league_id: string
  espn_game_id?: string
  game_date: string
  game_time?: string
  game_end_time?: string
  home_team_name?: string
  away_team_name?: string
  home_team_abbr?: string
  away_team_abbr?: string
  home_team_logo?: string
  away_team_logo?: string
  home_score: number
  away_score: number
  game_status: string
  period?: string
  clock?: string
  possession?: string
  is_red_zone: boolean
  down_distance?: string
  venue?: string
  broadcast?: string[]
}

export interface UpdateSportsGenericChannelRequest {
  channel_name?: string
  enabled?: boolean
  stream_id?: string
  panel_stream_id?: string
  panel_stream_name?: string
}

export interface MapGenericChannelRequest {
  stream_id?: string
  panel_stream_id?: string
  panel_stream_name?: string
}

// EPG Sync Status types
export interface EPGSyncStatusResponse {
  sources: EPGSourceSyncStatus[]
}

export interface EPGSourceSyncStatus {
  source_id: string
  source_name: string
  status: 'syncing' | 'completed' | 'error' | 'pending'
  last_sync?: string
  next_sync?: string
  error?: string
  program_count: number
  channel_count: number
  duration_ms: number
  sync_interval_hours: number
  sync_time?: string | null // "HH:MM" UTC if time-based sync
}

// XUI Import types
export interface XUICategory {
  id: string
  name: string
}

export interface XUIStream {
  id: string
  name: string
  category_id: string
  category_name: string
  icon: string
  epg_channel_id: string
  exists: boolean
}

export interface XUIStreamListResponse {
  streams: XUIStream[]
  categories: XUICategory[]
  total: number
}

export interface CategoryConfig {
  panel_category_id: string
  channel_range_start: number
  channel_range_end: number
  auto_assign_numbers: boolean
}

export interface ImportStreamsRequest {
  stream_ids: string[]
  category_id?: string
  create_category?: boolean
  category_configs?: CategoryConfig[]
  panel_url?: string
  panel_username?: string
  panel_password?: string
}

export interface ImportStreamsResponse {
  imported: number
  skipped: number
  errors?: string[]
}

// Broadcast Message types (Emby notifications)
export interface BroadcastConfig {
  emby_url: string
  emby_api_key?: string
  has_api_key: boolean
}

export interface BroadcastConfigRequest {
  emby_url: string
  emby_api_key?: string
}

export interface BroadcastMessage {
  id: string
  text: string
  timeout_ms?: number
  sent_at: string
  sessions_count?: number
}

export interface BroadcastSendRequest {
  text: string
  timeout_ms?: number
}

export interface BroadcastSendResponse {
  status: string
  sessions_total: number
  sessions_success: number
  message: BroadcastMessage
}

export interface BroadcastTestResponse {
  status: string
  sessions_count: number
  sessions: EmbySession[]
}

export interface EmbySession {
  Id: string
  DeviceId: string
  DeviceName: string
  UserName: string
  Client: string
}

export interface AssignChannelNumbersResponse {
  message: string
  assigned: number
  skipped: number
  categories: Array<{
    category: string
    assigned: number
    skipped: number
  }>
}

// YouTube types
export interface YouTubeChannel {
  id: string
  name: string
  description?: string
  youtube_url: string
  youtube_type: 'playlist' | 'channel'
  youtube_id?: string
  playback_mode: 'sequential' | 'shuffle' | 'loop'
  stream_id?: string
  category_id?: string
  current_video_index: number
  is_active: boolean
  auto_refresh: boolean
  refresh_interval_hours: number
  last_refreshed?: string
  last_played_at?: string
  video_count: number
  total_duration_seconds: number
  thumbnail_url?: string
  created_at: string
  updated_at: string
}

export interface YouTubeVideo {
  id: string
  channel_id: string
  youtube_video_id: string
  title: string
  description?: string
  duration_seconds: number
  position: number
  thumbnail_url?: string
  upload_date?: string
  view_count?: number
  is_available: boolean
  created_at: string
  updated_at: string
}

export interface YouTubeChannelListResponse {
  channels: YouTubeChannel[]
  total: number
  page: number
  per_page: number
}

export interface YouTubeVideoListResponse {
  videos: YouTubeVideo[]
  total: number
  page: number
  per_page: number
}

export interface YouTubeChannelStats {
  total_videos: number
  available_videos: number
  watched_videos: number
  total_duration_seconds: number
  watched_duration_seconds: number
  percent_watched: number
  last_watched_at?: string
}

export interface CreateYouTubeChannelRequest {
  name: string
  description?: string
  youtube_url: string
  playback_mode: 'sequential' | 'shuffle' | 'loop'
  category_id?: string
  auto_refresh?: boolean
  refresh_interval_hours?: number
}

export interface UpdateYouTubeChannelRequest {
  name?: string
  description?: string
  youtube_url?: string
  playback_mode?: 'sequential' | 'shuffle' | 'loop'
  category_id?: string
  is_active?: boolean
  auto_refresh?: boolean
  refresh_interval_hours?: number
}

// Client Settings & Device Logs types
export interface UserClientSettings {
  userId: string
  username: string
  device?: DeviceInfo
  lastSeen?: string
  settings: ClientSettingsPayload
  logCount: number
  updatedAt: string
}

export interface ClientSettingsPayload {
  debug_logging: boolean
  log_level: string
  log_upload_interval_seconds: number
  features: FeatureFlags
}

export interface FeatureFlags {
  mosaic: boolean
  recordings: boolean
  sports: boolean
}

export interface DeviceInfo {
  model?: string
  manufacturer?: string
  os_version?: string
  app_version?: string
  app_build?: number
}

export interface DeviceLogEntry {
  timestamp: string
  level: string
  tag: string
  message: string
}

export interface DeviceLogsResponse {
  userId: string
  username: string
  device: DeviceInfo
  logs: DeviceLogEntry[]
  total: number
  hasMore: boolean
}

export interface UpdateClientSettingsRequest {
  debug_logging?: boolean
  log_level?: string
  log_upload_interval_seconds?: number
  features?: FeatureFlags
}

// Panel Source types (multi-source sports EPG)
export interface SportsPanelSource {
  id: string
  name: string
  url: string
  username: string
  password: string
  category_filter?: string
  is_default: boolean
  enabled: boolean
  last_connected_at?: string
  last_error?: string
  stream_count: number
  created_at: string
  updated_at: string
}

export interface CreatePanelSourceRequest {
  name: string
  url: string
  username: string
  password: string
  category_filter?: string
  is_default?: boolean
}

export interface UpdatePanelSourceRequest {
  name?: string
  url?: string
  username?: string
  password?: string
  category_filter?: string
  enabled?: boolean
}

export interface PanelSourceTestResult {
  success: boolean
  message?: string
  error?: string
}

export interface PanelSourceStatus {
  id: string
  name: string
  connected: boolean
  last_check?: string
  stream_count: number
  consecutive_failures: number
}
